import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appointmentsApi } from '../api/appointments.api';
import type { Appointment, PartitionedAppointments } from '../types/appointment.types';

export const appointmentsQueryKey = (fhirId: string | null) => ['appointments', fhirId] as const;

/**
 * Appointments for a patient, split into upcoming / past.
 *
 * - upcoming (viitoare): future appointments, soonest first
 * - past (efectuate): finished appointments, most recent first
 */
export function useAppointments(patientFhirId: string | null) {
  const query = useQuery({
    queryKey: appointmentsQueryKey(patientFhirId),
    queryFn: () => appointmentsApi.getAppointments(patientFhirId!),
    enabled: Boolean(patientFhirId),
    staleTime: 60_000,
  });

  const partitioned = useMemo<PartitionedAppointments>(() => {
    const now = Date.now();
    const items = query.data ?? [];
    const upcoming = items
      .filter((a) => a.startTime.getTime() > now)
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    const past = items
      .filter((a) => a.endTime.getTime() <= now)
      .sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
    return { upcoming, past };
  }, [query.data]);

  return { ...query, ...partitioned };
}

export type { Appointment };
